/**
 * HeroSettingsPage
 *
 * Página dedicada aos banners do carrossel (hero) da loja.
 * Salva apenas home_banners — não mexe em sessões/tema da vitrine.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Image as ImageIcon,
  Save,
  Loader2,
  RotateCcw,
  AlertTriangle,
  Plus,
  Trash2,
  GripVertical,
  Monitor,
  Smartphone,
} from 'lucide-react'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useToast } from '../../../hooks/useToast'
import { Toast } from '../../../components'
import { erpSettingsService } from '../../../services/erpSettingsService'
import SplitBannerComposer from './SplitBannerComposer'
import {
  bannerHasBgPanels,
  createBgPanel,
  createBlankSplitStyle,
  normalizeBgPanels,
  normalizeSplitStyle,
  panelsToLegacyUrls,
} from './splitBannerDefaults'
import './ErpSettingsHubPage.css'
import './CockpitSettingsPage.css'
import './StorefrontSettingsPage.css'

const MAX_BANNERS = 8

function newBannerId() {
  return `banner-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

function cloneBanners(list = []) {
  return (list || []).map((item, index) => {
    const panels = normalizeBgPanels(item.bg_panels, item)
    const legacyUrls = panelsToLegacyUrls(panels)
    return {
      id: item.id || newBannerId(),
      layout: 'split',
      bg_panels: panels,
      ...legacyUrls,
      image_url_mobile: item.image_url_mobile || '',
      image_url_tablet: item.image_url_tablet || '',
      image_url_desktop: item.image_url_desktop || '',
      product_id: item.product_id || null,
      product_name: item.product_name || '',
      product_price: item.product_price ?? null,
      product_image_url: item.product_image_url || '',
      product_image_id: item.product_image_id || null,
      split_style: normalizeSplitStyle(item.split_style, panels.length),
      cta_label: item.cta_label || '',
      link_url: item.link_url || '',
      alt: item.alt || '',
      sort_order: Number.isFinite(Number(item.sort_order))
        ? Number(item.sort_order)
        : index,
      active: item.active !== false,
      device:
        item.device === 'desktop' || item.device === 'mobile'
          ? item.device
          : null,
      place: item.place === 'shelf' ? 'shelf' : 'hero',
      shelf_id:
        item.place === 'shelf' && item.shelf_id ? String(item.shelf_id) : '',
      shelf_anchor: item.shelf_anchor === 'below' ? 'below' : 'above',
    }
  })
}

function bannersEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

function bannerHasBg(banner) {
  return bannerHasBgPanels(banner)
}

function bannerDevice(banner) {
  return banner?.device === 'desktop' || banner?.device === 'mobile'
    ? banner.device
    : 'both'
}

function bannerMatchesDevice(banner, surface) {
  const device = bannerDevice(banner)
  return device === 'both' || device === surface
}

function bannerSlotKey(banner) {
  if (banner?.place === 'shelf' && banner.shelf_id) {
    const anchor = banner.shelf_anchor === 'below' ? 'below' : 'above'
    return `shelf:${banner.shelf_id}:${anchor}`
  }
  return `hero:${banner?.id || ''}`
}

function groupBanners(list, surface) {
  const groups = []
  const byKey = new Map()
  list.forEach((banner, index) => {
    if (!bannerMatchesDevice(banner, surface)) return
    const key = bannerSlotKey(banner)
    let group = byKey.get(key)
    if (!group) {
      group = { key, slides: [] }
      byKey.set(key, group)
      groups.push(group)
    }
    group.slides.push({ banner, index })
  })
  return groups
}

function blankBanner(surface, extras = {}) {
  return {
    id: newBannerId(),
    layout: 'split',
    bg_panels: [createBgPanel(), createBgPanel()],
    image_url: '',
    image_url_mobile: '',
    image_url_tablet: '',
    image_url_desktop: '',
    image_url_left: '',
    image_url_right: '',
    image_url_left_mobile: '',
    image_url_right_mobile: '',
    product_id: null,
    product_name: '',
    product_price: null,
    product_image_url: '',
    product_image_id: null,
    split_style: createBlankSplitStyle(2),
    cta_label: '',
    link_url: '',
    alt: '',
    sort_order: 0,
    active: true,
    device: surface,
    place: 'hero',
    shelf_id: '',
    shelf_anchor: 'above',
    ...extras,
  }
}

function buildBannersPayload(banners) {
  return banners.map((item, index) => {
    const panels = normalizeBgPanels(item.bg_panels, item)
    const legacyUrls = panelsToLegacyUrls(panels)
    return {
      id: item.id,
      layout: 'split',
      sort_order: index,
      bg_panels: panels,
      ...legacyUrls,
      image_url_mobile: null,
      image_url_tablet: null,
      image_url_desktop: null,
      product_id: item.product_id || null,
      product_image_id: item.product_image_id || null,
      product_image_url: null,
      split_style: normalizeSplitStyle(item.split_style, panels.length),
      cta_label: item.cta_label?.trim() || null,
      link_url: item.product_id ? `/products/${item.product_id}` : null,
      alt: String(item.product_name || '').trim().slice(0, 160),
      active: item.active !== false,
      device:
        item.device === 'desktop' || item.device === 'mobile'
          ? item.device
          : null,
      place: item.place === 'shelf' && item.shelf_id ? 'shelf' : 'hero',
      shelf_id:
        item.place === 'shelf' && item.shelf_id ? String(item.shelf_id) : null,
      shelf_anchor: item.shelf_anchor === 'below' ? 'below' : 'above',
    }
  })
}

export default function HeroSettingsPage() {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const composerFlushRefs = useRef({})
  const bannersRef = useRef([])
  const { hasPermission } = usePermissions()
  const { toast, showToast, hideToast } = useToast()
  const canEdit = hasPermission(MODULES.SETTINGS, ACTIONS.UPDATE)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [validationErrors, setValidationErrors] = useState([])
  const [savedBanners, setSavedBanners] = useState([])
  const [banners, setBanners] = useState([])
  const [uploadTargetIndex, setUploadTargetIndex] = useState(null)
  const [uploadTargetField, setUploadTargetField] = useState('image_url_mobile')
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null })
  const [surface, setSurface] = useState(null)
  const [shelves, setShelves] = useState([])
  const [activeTabBySlot, setActiveTabBySlot] = useState({})

  const isDirty = useMemo(
    () => !bannersEqual(banners, savedBanners),
    [banners, savedBanners]
  )

  const visible = useMemo(() => {
    if (surface !== 'desktop' && surface !== 'mobile') return []
    return banners
      .map((banner, index) => ({ banner, index }))
      .filter(({ banner }) => bannerMatchesDevice(banner, surface))
  }, [banners, surface])

  const groups = useMemo(() => {
    if (surface !== 'desktop' && surface !== 'mobile') return []
    return groupBanners(banners, surface)
  }, [banners, surface])

  const desktopCount = useMemo(
    () => banners.filter((banner) => bannerMatchesDevice(banner, 'desktop')).length,
    [banners]
  )
  const mobileCount = useMemo(
    () => banners.filter((banner) => bannerMatchesDevice(banner, 'mobile')).length,
    [banners]
  )

  useEffect(() => {
    bannersRef.current = banners
  }, [banners])

  /** Callback estável por banner — evita limpar o flush a cada render */
  const registerFlushByIdRef = useRef({})
  const getRegisterFlush = useCallback((bannerId) => {
    if (!registerFlushByIdRef.current[bannerId]) {
      registerFlushByIdRef.current[bannerId] = (fn) => {
        if (typeof fn === 'function') {
          composerFlushRefs.current[bannerId] = fn
        } else {
          delete composerFlushRefs.current[bannerId]
        }
      }
    }
    return registerFlushByIdRef.current[bannerId]
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const settingsRes = await erpSettingsService.getNamespace('storefront')
      if (!settingsRes?.success) {
        throw new Error(settingsRes?.message || 'Falha ao carregar hero')
      }
      const settings = settingsRes.data?.settings || {}
      const next = cloneBanners(settings.home_banners)
      setSavedBanners(next)
      setBanners(next)
      setShelves(
        (Array.isArray(settings.home_shelves) ? settings.home_shelves : [])
          .filter((shelf) => shelf && shelf.id && shelf.title)
          .map((shelf) => ({
            id: String(shelf.id),
            title:
              shelf.active === false
                ? `${String(shelf.title)} (inativa)`
                : String(shelf.title),
          }))
      )
      setMeta({
        updated_at: settingsRes.data?.updated_at || null,
        updated_by: settingsRes.data?.updated_by || null,
      })
    } catch (err) {
      console.error(err)
      setError('Não foi possível carregar os banners do hero.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const updateBanner = (index, patchOrFn) => {
    setBanners((prev) => {
      const next = cloneBanners(prev)
      const current = next[index]
      if (!current) return prev
      const patch =
        typeof patchOrFn === 'function' ? patchOrFn(current) : patchOrFn
      next[index] = {
        ...current,
        ...patch,
        split_style:
          patch?.split_style != null
            ? normalizeSplitStyle(patch.split_style)
            : current.split_style,
      }
      bannersRef.current = next
      return next
    })
  }

  const addBanner = () => {
    if (surface !== 'desktop' && surface !== 'mobile') return
    const count = banners.filter((banner) =>
      bannerMatchesDevice(banner, surface)
    ).length
    if (count >= MAX_BANNERS) {
      showToast(`Máximo de ${MAX_BANNERS} banners neste dispositivo.`, 'warning')
      return
    }
    setBanners((prev) =>
      cloneBanners([...prev, blankBanner(surface, { sort_order: prev.length })])
    )
  }

  const addSlideToGroup = (group) => {
    if (surface !== 'desktop' && surface !== 'mobile') return
    const count = banners.filter((banner) =>
      bannerMatchesDevice(banner, surface)
    ).length
    if (count >= MAX_BANNERS) {
      showToast(`Máximo de ${MAX_BANNERS} banners neste dispositivo.`, 'warning')
      return
    }
    const template = group.slides[0]?.banner
    if (!template || template.place !== 'shelf' || !template.shelf_id) return
    const created = blankBanner(surface, {
      place: 'shelf',
      shelf_id: template.shelf_id,
      shelf_anchor: template.shelf_anchor === 'below' ? 'below' : 'above',
    })
    const insertAfter = Math.max(...group.slides.map((slide) => slide.index))
    setBanners((prev) => {
      const next = cloneBanners(prev)
      next.splice(insertAfter + 1, 0, created)
      return next.map((item, i) => ({ ...item, sort_order: i }))
    })
    setActiveTabBySlot((prev) => ({ ...prev, [group.key]: created.id }))
  }

  const placeGroup = (group, patch) => {
    const current = group.slides.find(
      (slide) => slide.banner.id === activeTabBySlot[group.key]
    ) || group.slides[0]
    const indexes = new Set(group.slides.map((slide) => slide.index))
    setBanners((prev) => {
      const next = cloneBanners(prev)
      indexes.forEach((index) => {
        const item = next[index]
        if (!item) return
        next[index] = { ...item, ...patch }
      })
      bannersRef.current = next
      return next
    })
    const place = patch.place || current?.banner.place
    const shelfId =
      patch.shelf_id !== undefined ? patch.shelf_id : current?.banner.shelf_id
    const anchor =
      patch.shelf_anchor === 'below'
        ? 'below'
        : patch.shelf_anchor === 'above'
          ? 'above'
          : current?.banner.shelf_anchor === 'below'
            ? 'below'
            : 'above'
    if (place === 'shelf' && shelfId && current?.banner.id) {
      setActiveTabBySlot((prev) => ({
        ...prev,
        [`shelf:${shelfId}:${anchor}`]: current.banner.id,
      }))
    }
  }

  const removeBanner = (index) => {
    setBanners((prev) =>
      cloneBanners(prev.filter((_, i) => i !== index)).map((item, i) => ({
        ...item,
        sort_order: i,
      }))
    )
  }

  const moveGroup = (groupIndex, direction) => {
    if (surface !== 'desktop' && surface !== 'mobile') return
    setBanners((prev) => {
      const currentGroups = groupBanners(prev, surface)
      const target = groupIndex + direction
      if (target < 0 || target >= currentGroups.length) return prev
      const nextGroups = currentGroups.slice()
      const [moved] = nextGroups.splice(groupIndex, 1)
      nextGroups.splice(target, 0, moved)
      const ordered = nextGroups.flatMap((group) =>
        group.slides.map((slide) => slide.banner)
      )
      let cursor = 0
      const next = prev.map((item) =>
        bannerMatchesDevice(item, surface) ? ordered[cursor++] : item
      )
      return cloneBanners(next).map((item, i) => ({ ...item, sort_order: i }))
    })
  }

  const moveSlideInGroup = (group, index, direction) => {
    const pos = group.slides.findIndex((slide) => slide.index === index)
    const swap = group.slides[pos + direction]
    if (pos < 0 || !swap) return
    setBanners((prev) => {
      const next = cloneBanners(prev)
      const tmp = next[index]
      next[index] = next[swap.index]
      next[swap.index] = tmp
      return next.map((item, i) => ({ ...item, sort_order: i }))
    })
  }

  const leaveSurface = () => {
    if (isDirty && !window.confirm('Há alterações não salvas. Voltar mesmo assim?')) {
      return
    }
    setSurface(null)
  }

  const triggerUpload = (index, field = 'image_url_mobile') => {
    if (!canEdit || uploading) return
    setUploadTargetIndex(index)
    setUploadTargetField(field)
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || uploadTargetIndex == null) return
    setUploading(true)
    try {
      const res = await erpSettingsService.uploadStorefrontBanner(file)
      if (!res?.success || !res?.data?.url) {
        throw new Error(res?.message || 'Falha no upload')
      }
      const url = res.data.url
      const mediaType =
        res.data.media_type === 'video' ||
        String(res.data.resource_type || '').toLowerCase() === 'video'
          ? 'video'
          : 'image'
      // panel:0 | panel:0:mobile
      if (
        typeof uploadTargetField === 'string' &&
        uploadTargetField.startsWith('panel:')
      ) {
        const parts = uploadTargetField.split(':')
        const panelIndex = Number(parts[1])
        const isMobile = parts[2] === 'mobile'
        updateBanner(uploadTargetIndex, (current) => {
          const panels = normalizeBgPanels(current.bg_panels, current).map(
            (p, i) => {
              if (i !== panelIndex) return p
              if (isMobile) {
                // Não sobrescreve media_type do desktop com upload mobile
                return { ...p, image_url_mobile: url }
              }
              return { ...p, image_url: url, media_type: mediaType }
            }
          )
          return {
            bg_panels: panels,
            ...panelsToLegacyUrls(panels),
          }
        })
      } else {
        updateBanner(uploadTargetIndex, { [uploadTargetField]: url })
      }
      showToast(
        mediaType === 'video' ? 'Vídeo enviado.' : 'Imagem enviada.',
        'success'
      )
    } catch (err) {
      console.error(err)
      showToast(err?.response?.data?.message || 'Erro no upload.', 'error')
    } finally {
      setUploading(false)
      setUploadTargetIndex(null)
    }
  }

  const collectLatestBanners = () => {
    // Aplica drafts do composer de forma síncrona (não depende do setState)
    const drafts = {}
    Object.entries(composerFlushRefs.current).forEach(([id, fn]) => {
      if (typeof fn !== 'function') return
      try {
        const style = fn()
        if (style) drafts[id] = normalizeSplitStyle(style)
      } catch (err) {
        console.warn('[HeroSettings] flush falhou', id, err)
      }
    })

    const latest = (bannersRef.current || []).map((banner) => {
      const fromDraft = drafts[banner.id]
      return {
        ...banner,
        layout: 'split',
        split_style: normalizeSplitStyle(fromDraft || banner.split_style),
      }
    })
    bannersRef.current = latest
    setBanners(latest)
    return latest
  }

  const handleSave = async () => {
    if (!canEdit) return

    const latest = collectLatestBanners()
    // Descarta slides vazios (ex.: "Adicionar banner" sem mídia) e exige
    // só fundo — produto em destaque é opcional (hero só com imagem).
    const toSave = latest.filter((banner) => bannerHasBg(banner))
    const errors = []
    if (latest.length > 0 && toSave.length === 0) {
      errors.push('Adicione ao menos uma foto de fundo em um slide.')
    }
    setValidationErrors(errors)
    if (errors.length) return

    setSaving(true)
    try {
      const payloadBanners = buildBannersPayload(
        toSave.map((item, index) => ({ ...item, sort_order: index }))
      )
      // Garantia: split sempre leva split_style completo (desktop + mobile)
      payloadBanners.forEach((item) => {
        if (item.layout === 'split' && !item.split_style) {
          item.split_style = normalizeSplitStyle()
        }
      })

      const response = await erpSettingsService.updateNamespace('storefront', {
        home_banners: payloadBanners,
      })
      if (!response?.success) {
        throw new Error(response?.message || 'Falha ao salvar')
      }
      const settings = response.data?.settings || {}
      const saved = settings.home_banners || payloadBanners
      const missingStyle = (saved || []).some(
        (b) => b.layout === 'split' && !b.split_style
      )
      if (missingStyle) {
        console.error(
          '[HeroSettings] API devolveu banner split sem split_style. Reinicie o backend.'
        )
        showToast(
          'Backend desatualizado: reinicie a API e salve de novo.',
          'error'
        )
      }
      const next = cloneBanners(saved).map((item) => {
        const prev =
          toSave.find((b) => b.id && b.id === item.id) ||
          latest.find((b) => b.id && b.id === item.id)
        if (!prev || item.layout !== 'split') return item
        return {
          ...item,
          product_name: item.product_name || prev.product_name || '',
          product_price: item.product_price ?? prev.product_price ?? null,
          product_image_url:
            item.product_image_url || prev.product_image_url || '',
        }
      })
      setSavedBanners(next)
      setBanners(next)
      setMeta({
        updated_at: response.data?.updated_at || null,
        updated_by: response.data?.updated_by || null,
      })
      setValidationErrors([])
      showToast('Hero salvo com sucesso.', 'success')
    } catch (err) {
      console.error(err)
      const details = err?.response?.data?.details
      if (Array.isArray(details) && details.length) {
        setValidationErrors(details)
      }
      showToast(err?.response?.data?.message || 'Erro ao salvar hero.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setBanners(cloneBanners(savedBanners))
    setValidationErrors([])
  }

  if (loading) {
    return (
      <div className="erp-settings-page cockpit-settings-page">
        <div className="erp-settings-loading">
          <Loader2 className="cockpit-settings-spinner" size={28} />
          <p>Carregando hero...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="erp-settings-page cockpit-settings-page storefront-settings-page">
      <button
        type="button"
        className="cockpit-settings-back-btn"
        onClick={() =>
          surface ? leaveSurface() : navigate('/erp/configuracoes')
        }
      >
        <ArrowLeft size={14} />
        {surface ? 'Dispositivos' : 'Configurações'}
      </button>

      <header className="erp-settings-header">
        <div className="erp-settings-header-main">
          <div
            className="cockpit-settings-icon-badge"
            style={{ background: '#0f766e' }}
          >
            <ImageIcon size={22} />
          </div>
          <div>
            <h1 className="erp-settings-title">
              {surface === 'desktop'
                ? 'Hero / Desktop'
                : surface === 'mobile'
                  ? 'Hero / Mobile'
                  : 'Hero / Banners'}
            </h1>
            <p className="erp-settings-subtitle">
              {surface
                ? `Banners só deste dispositivo. O outro tamanho não recebe slide vazio.`
                : 'Escolha o dispositivo. Cada tamanho tem o próprio carrossel.'}
            </p>
          </div>
        </div>
        <div className="erp-settings-header-actions hero-settings-actions">
          {isDirty && canEdit ? (
            <span className="hero-settings-dirty">Alterações não salvas</span>
          ) : null}
          <button
            type="button"
            className="hero-settings-btn hero-settings-btn--undo"
            disabled={!canEdit || !isDirty || saving}
            onClick={handleReset}
          >
            <RotateCcw size={16} />
            Desfazer
          </button>
          <button
            type="button"
            className="hero-settings-btn hero-settings-btn--save"
            disabled={!canEdit || saving || !isDirty}
            onClick={handleSave}
          >
            {saving ? (
              <Loader2 size={16} className="cockpit-settings-spinner" />
            ) : (
              <Save size={16} />
            )}
            Salvar hero
          </button>
        </div>
      </header>

      {error ? (
        <div className="erp-settings-error-banner" role="alert">
          <p>{error}</p>
          <button type="button" onClick={load}>
            Tentar novamente
          </button>
        </div>
      ) : null}

      {validationErrors.length > 0 ? (
        <div className="storefront-settings-validation" role="alert">
          <AlertTriangle size={16} />
          <ul>
            {validationErrors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/mp4,video/webm,video/quicktime"
        hidden
        onChange={handleFileChange}
      />

      {!surface ? (
        <section className="hero-device-picker" aria-label="Escolher dispositivo">
          <button
            type="button"
            className="hero-device-card"
            onClick={() => setSurface('desktop')}
          >
            <span className="hero-device-card-icon">
              <Monitor size={28} />
            </span>
            <span className="hero-device-card-title">Desktop</span>
            <span className="hero-device-card-meta">
              {desktopCount === 0
                ? 'Nenhum banner'
                : `${desktopCount} banner${desktopCount === 1 ? '' : 's'}`}
            </span>
          </button>
          <button
            type="button"
            className="hero-device-card"
            onClick={() => setSurface('mobile')}
          >
            <span className="hero-device-card-icon">
              <Smartphone size={28} />
            </span>
            <span className="hero-device-card-title">Mobile</span>
            <span className="hero-device-card-meta">
              {mobileCount === 0
                ? 'Nenhum banner'
                : `${mobileCount} banner${mobileCount === 1 ? '' : 's'}`}
            </span>
          </button>
          <p className="hero-device-picker-note">
            Banners antigos, sem dispositivo definido, continuam nos dois até
            serem recriados em um tamanho só. Um slide sem foto naquele
            tamanho não entra no carrossel.
          </p>
        </section>
      ) : null}

      {surface ? (
      <section className="storefront-settings-section">
        <div className="storefront-settings-section-head">
          <h2>
            <ImageIcon size={18} />
            Slides do carrossel
          </h2>
          <p>
            Até {MAX_BANNERS} slides neste dispositivo. Um banner criado aqui
            não aparece no outro tamanho. Na vitrine, as fotos do mesmo lugar
            ficam em abas, em sequência.
          </p>
        </div>

        <div className="storefront-settings-list">
          {visible.length === 0 && (
            <div className="storefront-settings-empty">
              <p>Nenhum banner neste dispositivo.</p>
              <p>Adicione imagens só para este tamanho.</p>
            </div>
          )}

          {groups.map((group, groupIndex) => {
            const slide =
              group.slides.find(
                (item) => item.banner.id === activeTabBySlot[group.key]
              ) || group.slides[0]
            const banner = slide.banner
            const index = slide.index
            const isShelf = banner.place === 'shelf' && Boolean(banner.shelf_id)
            const slidePos = group.slides.findIndex((item) => item.index === index)
            const sequence = isShelf && group.slides.length > 1
            return (
              <article key={group.key} className="storefront-settings-card">
                {isShelf ? (
                  <div className="hero-slide-tabs" role="tablist" aria-label="Fotos desta posição">
                    {group.slides.map((item, tabIndex) => (
                      <button
                        key={item.banner.id}
                        type="button"
                        role="tab"
                        aria-selected={item.banner.id === banner.id}
                        className={
                          item.banner.id === banner.id
                            ? 'hero-slide-tab is-active'
                            : 'hero-slide-tab'
                        }
                        onClick={() =>
                          setActiveTabBySlot((prev) => ({
                            ...prev,
                            [group.key]: item.banner.id,
                          }))
                        }
                      >
                        Foto {tabIndex + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="hero-slide-tab-add"
                      disabled={!canEdit || visible.length >= MAX_BANNERS}
                      title="Adicionar foto nesta posição"
                      aria-label="Adicionar foto nesta posição"
                      onClick={() => addSlideToGroup(group)}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                ) : null}
                <div className="storefront-settings-card-toolbar">
                  <span className="storefront-settings-order">
                    <GripVertical size={14} />
                    {sequence ? `Foto ${slidePos + 1}` : `#${groupIndex + 1}`}
                  </span>
                  <div className="hero-place-controls" role="group" aria-label="Posição do banner">
                    <button
                      type="button"
                      className={banner.place === 'shelf' ? '' : 'is-active'}
                      disabled={!canEdit}
                      onClick={() =>
                        placeGroup(group, { place: 'hero', shelf_id: '' })
                      }
                    >
                      Hero
                    </button>
                    <button
                      type="button"
                      className={banner.place === 'shelf' ? 'is-active' : ''}
                      disabled={!canEdit || shelves.length === 0}
                      title={
                        shelves.length === 0
                          ? 'Cadastre uma seção na vitrine antes'
                          : 'Entre as seções da vitrine'
                      }
                      onClick={() =>
                        placeGroup(group, {
                          place: 'shelf',
                          shelf_id: banner.shelf_id || shelves[0]?.id || '',
                          shelf_anchor: banner.shelf_anchor || 'above',
                        })
                      }
                    >
                      Vitrine
                    </button>
                    {banner.place === 'shelf' ? (
                      <>
                        <select
                          value={banner.shelf_id || ''}
                          disabled={!canEdit}
                          aria-label="Seção da vitrine"
                          onChange={(e) =>
                            placeGroup(group, { shelf_id: e.target.value })
                          }
                        >
                          {banner.shelf_id &&
                          !shelves.some((shelf) => shelf.id === banner.shelf_id) ? (
                            <option value={banner.shelf_id}>Seção removida</option>
                          ) : null}
                          {shelves.map((shelf) => (
                            <option key={shelf.id} value={shelf.id}>
                              {shelf.title}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className={banner.shelf_anchor === 'below' ? '' : 'is-active'}
                          disabled={!canEdit}
                          onClick={() =>
                            placeGroup(group, { shelf_anchor: 'above' })
                          }
                        >
                          Acima
                        </button>
                        <button
                          type="button"
                          className={banner.shelf_anchor === 'below' ? 'is-active' : ''}
                          disabled={!canEdit}
                          onClick={() =>
                            placeGroup(group, { shelf_anchor: 'below' })
                          }
                        >
                          Abaixo
                        </button>
                      </>
                    ) : null}
                  </div>
                  <div className="storefront-settings-card-actions">
                    <button
                      type="button"
                      className="storefront-settings-icon-btn"
                      disabled={
                        !canEdit ||
                        (sequence ? slidePos === 0 : groupIndex === 0)
                      }
                      onClick={() =>
                        sequence
                          ? moveSlideInGroup(group, index, -1)
                          : moveGroup(groupIndex, -1)
                      }
                      aria-label={
                        sequence
                          ? 'Mover esta foto para a esquerda na sequência'
                          : 'Mover para cima'
                      }
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="storefront-settings-icon-btn"
                      disabled={
                        !canEdit ||
                        (sequence
                          ? slidePos === group.slides.length - 1
                          : groupIndex === groups.length - 1)
                      }
                      onClick={() =>
                        sequence
                          ? moveSlideInGroup(group, index, 1)
                          : moveGroup(groupIndex, 1)
                      }
                      aria-label={
                        sequence
                          ? 'Mover esta foto para a direita na sequência'
                          : 'Mover para baixo'
                      }
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="storefront-settings-icon-btn storefront-settings-icon-btn--danger"
                      disabled={!canEdit}
                      onClick={() => removeBanner(index)}
                      aria-label={sequence ? 'Remover esta foto' : 'Remover banner'}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="storefront-settings-card-body storefront-settings-card-body--banners">
                  <SplitBannerComposer
                    banner={banner}
                    canEdit={canEdit}
                    lockedMode={surface}
                    uploading={uploading && uploadTargetIndex === index}
                    uploadTargetField={
                      uploadTargetIndex === index ? uploadTargetField : null
                    }
                    onChange={(partial) => updateBanner(index, partial)}
                    onUpload={(field) => triggerUpload(index, field)}
                    registerFlush={getRegisterFlush(banner.id)}
                  />
                  <label className="storefront-settings-label">
                    Texto do botão (opcional)
                    <input
                      type="text"
                      value={banner.cta_label || ''}
                      disabled={!canEdit}
                      onChange={(e) =>
                        updateBanner(index, { cta_label: e.target.value })
                      }
                      placeholder="Ver produto"
                      maxLength={60}
                    />
                  </label>

                  <div className="storefront-settings-fields">
                    <div className="storefront-settings-upload-row">
                      <label className="storefront-settings-active">
                        <input
                          type="checkbox"
                          checked={banner.active !== false}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateBanner(index, { active: e.target.checked })
                          }
                        />
                        Ativo
                      </label>
                    </div>
                  </div>
                </div>
              </article>
            )
          })}
        </div>

        <button
          type="button"
          className="storefront-settings-add-btn storefront-settings-add-btn--inline"
          disabled={!canEdit || visible.length >= MAX_BANNERS}
          onClick={addBanner}
        >
          <Plus size={16} />
          Adicionar banner
        </button>
      </section>
      ) : null}

      {meta.updated_at ? (
        <p className="cockpit-settings-meta">
          Última atualização:{' '}
          {new Date(meta.updated_at).toLocaleString('pt-BR')}
        </p>
      ) : null}

      <Toast toast={toast} onClose={hideToast} />
    </div>
  )
}
